import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  inject,
  Input,
  Output,
  ViewChild,
} from '@angular/core';

@Component({
  selector: 'app-inactivity-modal',
  templateUrl: './inactivity-modal.component.html',
  styleUrls: ['./inactivity-modal.component.scss'],
  standalone: false,
})
export class InactivityModalComponent implements AfterViewInit {
  @ViewChild('dialog', { static: true }) dialogRef!: ElementRef<HTMLDivElement>;
  @ViewChild('primaryButton') primaryButtonRef!: ElementRef<HTMLButtonElement>;
  @ViewChild('secondaryButton') secondaryButtonRef!: ElementRef<HTMLButtonElement>;
  @Input() totalSeconds = 120;

  @Input()
  set remainingSeconds(value: number) {
    this._remainingSeconds = value ?? 0;
  }
  get remainingSeconds(): number {
    return this._remainingSeconds;
  }

  @Output() continueSession = new EventEmitter<void>();
  @Output() logoutSession = new EventEmitter<void>();

  private _remainingSeconds = 120;

  // Geometría del anillo SVG
  readonly circleRadius = 54;
  readonly strokeWidth = 1;
  readonly viewBoxSize = 120;
  readonly circumference = 2 * Math.PI * this.circleRadius;
  private readonly changeDetectorRef = inject(ChangeDetectorRef);

  /** Indica si el contador está en un estado crítico (últimos 5 segundos). */
  get isCritical(): boolean {
    return this.remainingSeconds <= 5;
  }

  /** Fracción de tiempo transcurrido (0 al inicio, 1 al final). */
  get elapsedRatio(): number {
    if (!this.totalSeconds || this.totalSeconds <= 0) {
      return 0;
    }

    const elapsed = this.totalSeconds - this.remainingSeconds;
    const ratio = elapsed / this.totalSeconds;
    return Math.min(Math.max(ratio, 0), 1);
  }

  get pointerCx(): number {
    const center = this.viewBoxSize / 2;
    const angle = 2 * Math.PI * this.elapsedRatio;
    return center + this.circleRadius * Math.cos(angle);
  }

  get pointerCy(): number {
    const center = this.viewBoxSize / 2;
    const angle = 2 * Math.PI * this.elapsedRatio;
    return center + this.circleRadius * Math.sin(angle);
  }

  ngAfterViewInit(): void {
    const buttonEl = this.primaryButtonRef?.nativeElement;
    const dialogEl = this.dialogRef?.nativeElement;

    if (buttonEl) {
      buttonEl.focus();
    } else if (dialogEl) {
      dialogEl.focus();
    }
  }

  @HostListener('keydown', ['$event'])
  onKeydown(event: KeyboardEvent): void {
    if (event.key !== 'Tab') {
      return;
    }

    const first = this.primaryButtonRef?.nativeElement;
    const last = this.secondaryButtonRef?.nativeElement ?? first;

    if (!first || !last) {
      return;
    }

    const active = document.activeElement;
    const isShift = event.shiftKey;

    // Ciclar el foco entre los botones dentro del diálogo.
    if (!isShift && active === last) {
      event.preventDefault();
      first.focus();
    } else if (isShift && active === first) {
      event.preventDefault();
      last.focus();
    }
  }

  get minutes(): string {
    const minutes = Math.floor(this.remainingSeconds / 60);
    return this.formatTimeValue(minutes);
  }

  get seconds(): string {
    const seconds = this.remainingSeconds % 60;
    return this.formatTimeValue(seconds);
  }

  /** Devuelve el offset del trazo para representar el tiempo restante. */
  get progressCircleOffset(): number {
    if (!this.totalSeconds || this.totalSeconds <= 0) {
      return this.circumference;
    }

    // Usamos el tiempo transcurrido para que el círculo se rellene desde las 12 en sentido horario.
    const ratio = this.elapsedRatio;
    return this.circumference * (1 - ratio);
  }

  /**
   * Actualiza la cuenta atrás desde el servicio y fuerza el refresco inmediato del modal.
   */
  updateRemainingSeconds(value: number): void {
    const nextValue = value ?? 0;

    if (this._remainingSeconds === nextValue) {
      return;
    }

    this._remainingSeconds = nextValue;
    this.changeDetectorRef.detectChanges();
  }

  onContinue(): void {
    this.continueSession.emit();
  }

  onLogout(): void {
    this.logoutSession.emit();
  }

  private formatTimeValue(value: number): string {
    return value < 10 ? `0${value}` : `${value}`;
  }
}
